 import { motion } from "framer-motion";
 import { format, parseISO, differenceInDays, addDays, isAfter, isBefore, startOfDay } from "date-fns";
 import { Sprout, Moon, Settings, LogOut, ChevronLeft, ChevronRight } from "lucide-react";
 import { Button } from "@/components/ui/button";
 import { getPeriodForDay } from "@/data/dailyDuas";
 
 interface DayHeaderProps {
   currentDay: number;
   ramadanStartDate?: string;
   streak: number;
   onDayChange: (day: number) => void;
   onSettings: () => void;
   onLogout: () => void;
   userName?: string;
   isGuest?: boolean;
 }
 
 export const DayHeader = ({
   currentDay,
   ramadanStartDate,
   streak,
   onDayChange,
   onSettings,
   onLogout,
   userName,
   isGuest,
 }: DayHeaderProps) => {
   const period = getPeriodForDay(currentDay);
   
   // Calculate the maximum unlocked day based on ramadanStartDate and today's date
   const getMaxUnlockedDay = () => {
     if (!ramadanStartDate) {
       return 30; // If no start date set, allow all days (fallback)
     }
     
     const startDate = startOfDay(parseISO(ramadanStartDate));
     const today = startOfDay(new Date());
     
     // If before Ramadan starts, no days unlocked yet (but allow day 1 for preview)
     if (isBefore(today, startDate)) {
       return 1;
     }
     
     // Calculate which day of Ramadan it is (1-indexed)
     const daysSinceStart = differenceInDays(today, startDate) + 1;
     
     // Clamp to 1-30 range
     return Math.min(Math.max(daysSinceStart, 1), 30);
   };
 
   const maxUnlockedDay = getMaxUnlockedDay();
   const isOnLatestDay = currentDay >= maxUnlockedDay;
 
   // Calculate days remaining
   const getDaysInfo = () => {
     if (!ramadanStartDate) {
       return { daysRemaining: 30 - currentDay, isRamadan: true };
     }
     
     const startDate = parseISO(ramadanStartDate);
     const today = new Date();
     const endDate = addDays(startDate, 29);
     
     if (isBefore(today, startDate)) {
       return { daysRemaining: differenceInDays(startDate, today), isRamadan: false, beforeRamadan: true };
     }
     
     if (isAfter(today, endDate)) {
       return { daysRemaining: 0, isRamadan: false };
     }
     
     return { daysRemaining: 30 - currentDay, isRamadan: true };
   };
 
   const { daysRemaining, isRamadan } = getDaysInfo();
 
   const handlePrevDay = () => {
     if (currentDay > 1) onDayChange(currentDay - 1);
   };
 
   const handleNextDay = () => {
     if (currentDay < 30 && currentDay < maxUnlockedDay) {
       onDayChange(currentDay + 1);
     }
   };
 
   return (
     <div className="space-y-4">
       {/* Top bar */}
       <header className="flex justify-between items-center">
         <div className="flex items-center gap-3">
           <span className="text-2xl" style={{ color: 'hsl(var(--primary))' }}>☽</span>
           <div>
             <h1 className="font-display text-lg text-foreground">Ramadan Renewal</h1>
             <p className="text-xs text-muted-foreground">
               {isGuest ? 'Guest Mode' : userName || 'Welcome back'}
             </p>
           </div>
         </div>
         <div className="flex items-center gap-1">
           <Button variant="ghost" size="icon" onClick={onSettings}>
             <Settings className="w-4 h-4" />
           </Button>
           <Button variant="ghost" size="icon" onClick={onLogout}>
             <LogOut className="w-4 h-4" />
           </Button>
         </div>
       </header>
 
       {/* Day Navigation Card */}
       <motion.div
         initial={{ opacity: 0, y: 20 }}
         animate={{ opacity: 1, y: 0 }}
         className="bg-gradient-to-br from-primary to-primary/80 rounded-xl p-5 text-primary-foreground"
       >
         <div className="flex justify-between items-start mb-3">
           <div>
             <p className="text-xs uppercase tracking-[0.2em] text-primary-foreground/60 mb-1">
               {format(new Date(), 'EEEE, d MMMM')}
             </p>
             <h2 className="font-display text-2xl">
               Day {currentDay}
             </h2>
             {period && (
               <p className="text-sm text-primary-foreground/80 mt-1">
                 {period.name} • {period.theme}
               </p>
             )}
           </div>
           
           {/* Day Navigation */}
           <div className="flex items-center gap-1 bg-primary-foreground/10 rounded-full p-1">
             <button
               onClick={handlePrevDay}
               disabled={currentDay === 1}
               className="p-1.5 rounded-full hover:bg-primary-foreground/20 disabled:opacity-30 transition-colors"
             >
               <ChevronLeft className="w-4 h-4" />
             </button>
             <span className="text-sm font-medium px-2">{currentDay}/30</span>
             <button
               onClick={handleNextDay}
               disabled={currentDay === 30 || isOnLatestDay}
               className={`p-1.5 rounded-full transition-colors ${
                 isOnLatestDay 
                   ? 'opacity-30 cursor-not-allowed' 
                   : 'hover:bg-primary-foreground/20 disabled:opacity-30'
               }`}
             >
               <ChevronRight className="w-4 h-4" />
             </button>
           </div>
         </div>
 
         <div className="flex items-center justify-between">
           {isRamadan && daysRemaining > 0 && (
             <p className="text-sm text-primary-foreground/60">
               {daysRemaining} days remaining
             </p>
           )}
           
           <div className="flex items-center gap-3">
            <motion.div 
                className={`flex items-center gap-1.5 rounded-full px-3 py-1 ${
                  streak > 0 
                    ? 'bg-primary-foreground/15 border border-primary-foreground/20' 
                    : 'bg-primary-foreground/10'
                }`}
                animate={streak > 0 ? { scale: [1, 1.05, 1] } : {}}
                transition={{ duration: 0.6, delay: 0.5 }}
              >
                {streak > 0 ? (
                  <span className="text-base">🔥</span>
                ) : (
                  <Sprout className="w-4 h-4 text-primary-foreground/50" />
                )}
                <span className="text-sm font-medium">
                  {streak > 0 ? `${streak} day streak` : 'Start a streak!'}
                </span>
              </motion.div>
             
             {period?.id === 3 && (
               <div className="flex items-center gap-1.5 bg-primary-foreground/10 rounded-full px-3 py-1">
                 <Moon className="w-4 h-4" style={{ color: 'hsl(270, 70%, 80%)' }} />
                 <span className="text-sm">Last 10</span>
               </div>
             )}
           </div>
         </div>
       </motion.div>
     </div>
   );
 };